<script lang="ts">
  import type { CTAOptions } from "@kontextwerk/web-sdk/ui/actions/cta/types"
  import { mdiArchiveOutline } from "@mdi/js"
  import { DEFAULT_ORGANIZATION_TIMEZONE } from "../../../../../../../../../../../_shared/_logic/timezones"
  import { defaultPlanningStartPreset } from "../../../../../../../../../../_shared/_logic/planning-start-preset"
  import AppointmentPlanningSurface from "../../../../../../../../../../_shared/_modules/appointment-planning-surface"
  import { appointmentPlanningDomainOperations } from "../../../../../../../../domain-operations"
  import type { AppointmentPlanningSubmissionResult } from "../../../../../../../../../../_shared/_logic/appointments-contract"
  import type {
    BatchUploadCancellationAction,
    BatchUploadInit,
    BatchUploadReferenceData,
    BatchUploadStepContext,
    BatchUploadWorkflowEvent,
    CreateWorkOrdersOutput,
    PlanAppointmentsOutput,
  } from "../../_shared/_logic/batch-upload-contract"

  type Props = {
    cancellationAction?: BatchUploadCancellationAction
    ctx: BatchUploadStepContext
    init: BatchUploadInit
    referenceData: BatchUploadReferenceData
    loadList: CreateWorkOrdersOutput
    output?: PlanAppointmentsOutput
    onEvent: (event: BatchUploadWorkflowEvent) => void | Promise<void>
  }

  const {
    ctx,
    init,
    referenceData,
    loadList,
    output = undefined,
    cancellationAction = undefined,
    onEvent,
  }: Props = $props()

  const submitOptions = $derived<CTAOptions | undefined>(
    !ctx.readOnly && cancellationAction
      ? {
          label: "Optionen zur Sammelplanung",
          items: [
            {
              id: "cancel-batch-planning",
              label: cancellationAction.label,
              icon: mdiArchiveOutline,
              tone: "danger",
              onClick: () => {
                void onEvent({ type: "cancel_requested" })
              },
            },
          ],
        }
      : undefined,
  )

  const completePlanning = async (submission: AppointmentPlanningSubmissionResult) => {
    const result = submission.result

    if (submission.kind === "committed") {
      await init.onWorkflowMutated?.(loadList.createdRows.map((row) => row.workOrder))
    }

    await onEvent({
      type:
        submission.kind === "committed" && submission.status === "partial"
          ? "planning_progress"
          : "planning_completed",
      output: {
        settings: result.settings,
        plannedRows: result.plannedRows,
        createdEvents: result.plannedRows.flatMap((row) => (row.event ? [row.event] : [])),
        planningRowResults: result.response?.row_results ?? output?.planningRowResults ?? [],
      },
    })

    if (submission.kind !== "committed" || submission.status === "completed")
      await ctx.requestNext?.()
  }
</script>

<AppointmentPlanningSurface
  organizationId={init.organizationId}
  trialRestrictionsActive={init.trialRestrictionsActive}
  existingWorkOrderRows={loadList.createdRows.map((row) => ({
    id: row.rowId,
    workOrder: row.workOrder,
    property: row.context.property,
  }))}
  existingSubmitLabel="Weiter zu Kunden informieren"
  {submitOptions}
  externalActionState={cancellationAction}
  onRowsRemoved={() => {}}
  unitContactContexts={loadList.unitContactContexts}
  initialSettings={output?.settings ?? null}
  initialExistingPlannedRows={output?.plannedRows ?? []}
  orderTypes={init.orderTypes}
  employeeOptions={referenceData.employeeOptions}
  workingHoursByUser={init.workingHoursByUser ?? {}}
  flexSlotsPerDay={referenceData.planningSettings?.flex_slots_per_day ?? 0}
  defaultStartDate={referenceData.defaultStartDate}
  defaultStartPreset={defaultPlanningStartPreset()}
  settingsTimezone={referenceData.planningSettings?.timezone ?? DEFAULT_ORGANIZATION_TIMEZONE}
  operations={appointmentPlanningDomainOperations}
  readOnly={ctx.readOnly}
  helpModalTextId="workOrderBatchPlanAppointments"
  setError={ctx.setError}
  onSubmissionResult={completePlanning}
  onPreviewIntegrityError={() => void onEvent({ type: "planning_preview_invalid" })}
/>
